import { useState, useRef, FormEvent } from 'react';
import TopBar from '@/components/feature/TopBar';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('contact_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }

    // Remove honeypot from data
    formData.delete('contact_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9akh1cnib0mhnm9k5lg', {
        method: 'POST',
        body: formData,
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        (parsed?.meta as Record<string, string>)?.message ||
        (parsed?.meta as Record<string, string>)?.detail ||
        (parsed?.message as string) ||
        responseText;

      const isSpam =
        typeof serverMsg === 'string' &&
        (serverMsg.toLowerCase().includes('spam') || serverMsg.toLowerCase().includes('form data is spam'));

      if (response.ok && (parsed?.code === 'OK' || (parsed?.code as string)) && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg(typeof serverMsg === 'string' && serverMsg ? serverMsg : 'Ocurrió un error al enviar el mensaje. Intenta nuevamente.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('No se pudo conectar con el servidor. Verifica tu conexión e intenta nuevamente.');
    }
  };

  return (
    <>
      <TopBar />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden py-20 md:py-28 px-4 md:px-8 lg:px-12 bg-background-100">
          <div className="absolute inset-0 texture-grid opacity-60" />
          <div className="absolute -top-16 -right-16 w-96 h-96 bg-primary-100/50 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-accent-200/40 rounded-full blur-3xl" />
          <div className="relative max-w-4xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50 text-primary-600 text-xs font-bold uppercase tracking-[0.15em] mb-6 border border-primary-200">
              <span className="w-2 h-2 rounded-full bg-accent-500" />
              Contacto
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium text-foreground-950 mb-6 leading-[1.05]">
              Estamos para <span className="italic text-primary-600">atenderte</span>
            </h1>
            <p className="text-foreground-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
              Ya sea para agendar una consulta, hacer una pregunta o recibir atención de emergencia, estamos a tu disposición las 24 horas del día.
            </p>
          </div>
        </section>

        {/* Contact Info + Form */}
        <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 md:gap-12">
              {/* Info Cards */}
              <div className="lg:col-span-2 space-y-5">
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-3">
                    <i className="ri-phone-line text-lg" />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">Teléfono</h3>
                  <a href="tel:+59177173439" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                    77173439
                  </a>
                </div>

                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-3">
                    <i className="ri-map-pin-line text-lg" />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">Dirección</h3>
                  <p className="text-sm text-foreground-600">
                    Junín 1152, atrás del Hospital Regional San Juan de Dios, Tarija, Bolivia
                  </p>
                </div>

                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-3">
                    <i className="ri-time-line text-lg" />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">Horarios</h3>
                  <p className="text-sm text-foreground-600">
                    Atención las 24 horas, los 7 días de la semana.
                  </p>
                </div>

                <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-3">
                    <i className="ri-whatsapp-line text-lg" />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">WhatsApp</h3>
                  <a
                    href="https://wa.me/59177173439"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                  >
                    Escribir al 77173439
                  </a>
                </div>
              </div>

              {/* Form */}
              <div className="lg:col-span-3">
                <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                    Envíanos un mensaje
                  </h2>
                  <p className="text-sm text-foreground-600 mb-6">
                    Completa el formulario y nos pondremos en contacto contigo a la brevedad.
                  </p>

                  <form
                    ref={formRef}
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="space-y-4"
                  >
                    {/* Honeypot */}
                    <div className="honeypot-field">
                      <input
                        type="text"
                        name="contact_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="nombre" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          Nombre completo
                        </label>
                        <input
                          type="text"
                          id="nombre"
                          name="nombre"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                          placeholder="Tu nombre"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          Correo electrónico
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                          placeholder="tu@email.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="telefono" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                        placeholder="Ej: 77173439"
                      />
                    </div>

                    <div>
                      <label htmlFor="mensaje" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Mensaje
                      </label>
                      <textarea
                        id="mensaje"
                        name="mensaje"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"
                        placeholder="¿En qué podemos ayudarte?"
                      />
                      <p className="text-xs text-foreground-400 mt-1 text-right">Máximo 500 caracteres</p>
                    </div>

                    <button
                      type="submit"
                      disabled={status === 'sending'}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap"
                    >
                      {status === 'sending' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          Enviar mensaje
                          <i className="ri-send-plane-line" />
                        </>
                      )}
                    </button>

                    {status === 'success' && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-green-50 text-green-700 text-sm">
                        <i className="ri-check-line mt-0.5 flex-shrink-0" />
                        <p>¡Mensaje enviado correctamente! Nos pondremos en contacto contigo pronto.</p>
                      </div>
                    )}

                    {status === 'error' && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                        <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />
                        <p>{errorMsg}</p>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="px-4 md:px-8 lg:px-12 pb-16 md:pb-24 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="rounded-xl overflow-hidden border border-background-200/70">
              <iframe
                title="Ubicación Clínica Vitalmedic Tarija"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1595.0!2d-64.725402!3d-21.5295984!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9406490078a97bd1%3A0x7062a16232158d3c!2sCL%C3%8DNICA%20TARIJA%20vitalmedic%20SRL!5e0!3m2!1ses!2sbo!4v1700000000000"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
              />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}